import type { ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeSuperSplashLargePrimaryContentItemContent$key } from "~/queries/__generated__/homeSuperSplashLargePrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  CoverImageStyledEntityLink,
  Headline,
  HeadlineContainer,
  LinkHighlightArea,
  LiveTagContainer,
  StyledEntityLink,
  SubContentInDesktop,
  SubHeadline,
} from "./styles";

export type Props = {
  children?: ReactNode;
  reference: homeSuperSplashLargePrimaryContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemHomeSuperSplashLargePrimary = forwardRef<HTMLDivElement, Props>(
  ({ children, className, onClick, provider, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeSuperSplashLargePrimaryContentItemContent on Content {
          ...entityLink
          entityId
        }
      `,
      reference_,
    );

    return (
      <Container className={className} ref={reference}>
        <CoverImageStyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
          <CoverImage>
            {provider.coverImage({
              responsiveVariants: {
                desktopUp: "size1200x800",
                homeDesktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size1200x800",
              },
              withCaption: true,
            })}
          </CoverImage>
        </CoverImageStyledEntityLink>
        <Content>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <LinkHighlightArea>
              <HeadlineContainer>
                <Headline>{provider.headline({ preferSocialHeadline: true })}</Headline>
                <LiveTagContainer>{provider.liveTag()}</LiveTagContainer>
              </HeadlineContainer>
              <SubHeadline>{provider.subHeadline()}</SubHeadline>
            </LinkHighlightArea>
          </StyledEntityLink>
          <ActionBar>
            {provider.actionBar({
              isBookmarkVisible: false,
            })}
          </ActionBar>
          <SubContentInDesktop>{children}</SubContentInDesktop>
        </Content>
      </Container>
    );
  },
);

ContentItemHomeSuperSplashLargePrimary.displayName = "ContentItemHomeSuperSplashLargePrimary";
